import http from "./http";

/**
 * PRICE PLANS — bảng giá tính từ dữ liệu quét đối thủ, chỉ đổi giá thật khi bấm "Apply".
 * Backend: POST/GET /admin/price-plans… (đang dựng song song — mọi kiểu dữ liệu gom ở file này
 * để khi hợp đồng API đổi chỉ phải sửa một chỗ).
 *
 * Giá trong plan đều là giá ALL-IN USD.
 */

export type PricePlanGroup = "N1" | "N2" | "N3" | "N4" | string;

export interface PricePlanPrices {
	small: number | null;
	standard: number | null;
	large: number | null;
	protection: number | null;
}

export interface PricePlanRow {
	partnerId: number;
	partnerName: string;
	country: string | null;
	city: string | null;
	zoneId: number | string | null;
	zoneName: string | null;
	group: PricePlanGroup | null;
	note: string | null;
	current: PricePlanPrices;
	next: PricePlanPrices;
	/** % thay đổi giá standard (vd 12.5 = +12,5%). */
	changePct: number | null;
}

export interface PricePlanConfig {
	A: number;
	B: number;
	C: number;
	protection: number;
	capStandard: number | null;
	capLarge: number | null;
}

export interface PricePlanSummary {
	stations: number;
	up: number;
	down: number;
	same: number;
	maxDropPct: number | null;
	maxRisePct: number | null;
	/** Số trạm đổi giá > 40% — bắt buộc tick xác nhận khi Apply. */
	bigChanges: number;
}

export interface PricePlan {
	id: number | string;
	createdAt: string;
	createdBy: string | number | null;
	hash: string;
	config: PricePlanConfig;
	warnings: string[];
	summary: PricePlanSummary;
	rows: PricePlanRow[];
}

export interface PricePlanValidationError {
	partnerId: number | string | null;
	error: string;
}

export interface PricePlanApplyResult {
	ok: boolean;
	applied: number;
	durationMs: number;
	snapshotId: number | string;
}

export interface PricePlanHistoryItem {
	snapshotId: number | string;
	planId: number | string | null;
	appliedAt: string;
	appliedBy: string | number | null;
	count: number;
	kind: "apply" | "rollback";
	/** applying | applied | failed_reverted | failed_revert_incomplete */
	status?: string | null;
}

export interface PricePlanRollbackResult {
	ok: boolean;
	applied: number;
	snapshotId: number | string;
}

/** Lỗi API có kèm danh sách lỗi từng trạm (4xx khi upload plan). */
export class PricePlanApiError extends Error {
	status: number | null;
	errors: PricePlanValidationError[];
	constructor(message: string, status: number | null, errors: PricePlanValidationError[]) {
		super(message);
		this.name = "PricePlanApiError";
		this.status = status;
		this.errors = errors;
		// ES5 target: giữ đúng prototype để instanceof hoạt động
		Object.setPrototypeOf(this, PricePlanApiError.prototype);
	}
}

/* ───── Chuyển dữ liệu máy chủ → kiểu của màn hình ─────
 * Máy chủ (whalelo-backend services/pricing) bọc mọi kết quả trong { data }, lỗi là
 * { message, code, details }. Tên trường khác màn hình ⇒ đổi ở đúng một chỗ này. */
const num = (v: any): number | null => (v === null || v === undefined || v === "" || !Number.isFinite(Number(v)) ? null : Number(v));
const who = (v: any): string | null => (v && typeof v === "object" ? v.email || (v.userId != null ? `#${v.userId}` : null) : v ?? null);

const mapRow = (r: any): PricePlanRow => ({
	partnerId: Number(r?.partnerId),
	partnerName: r?.partnerName || `#${r?.partnerId}`,
	country: r?.country ?? null,
	city: r?.city ?? null,
	zoneId: r?.zoneId ?? null,
	zoneName: r?.zoneName ?? null,
	group: r?.group ?? null,
	note: r?.note ?? null,
	current: {
		small: num(r?.current?.small),
		standard: num(r?.current?.standard),
		large: num(r?.current?.large),
		protection: num(r?.current?.insuranceFee ?? r?.current?.protection),
	},
	next: r?.next
		? { small: num(r.next.small), standard: num(r.next.standard), large: num(r.next.large), protection: num(r.next.protection) }
		: { small: num(r?.small), standard: num(r?.standard), large: num(r?.large), protection: num(r?.protection) },
	changePct: num(r?.changePct ?? r?.change?.standardPct),
});

export const mapPlan = (p: any): PricePlan | null => {
	if (!p) return null;
	return {
		id: p.id,
		createdAt: p.createdAt,
		createdBy: who(p.createdBy),
		hash: p.hash,
		config: {
			A: Number(p.config?.A),
			B: Number(p.config?.B),
			C: Number(p.config?.C),
			protection: Number(p.config?.protection),
			capStandard: num(p.config?.capStandard),
			capLarge: num(p.config?.capLarge),
		},
		warnings: Array.isArray(p.warnings) ? p.warnings.map(String) : [],
		summary: {
			stations: Number(p.summary?.stations) || 0,
			up: Number(p.summary?.up) || 0,
			down: Number(p.summary?.down) || 0,
			same: Number(p.summary?.same) || 0,
			maxDropPct: num(p.summary?.maxDropPct),
			maxRisePct: num(p.summary?.maxRisePct),
			bigChanges: Number(p.summary?.bigChanges) || 0,
		},
		rows: Array.isArray(p.rows) ? p.rows.map(mapRow) : [],
	};
};

const unwrap = (d: any) => (d && typeof d === "object" && "data" in d ? d.data : d);

/** Lỗi axios → PricePlanApiError (message ngắn tiếng Anh + errors[] nếu có). */
export const toPricePlanError = (e: unknown): PricePlanApiError => {
	if (e instanceof PricePlanApiError) return e;
	const resp = (e as { response?: { status?: number; data?: any } })?.response;
	const data = resp?.data || {};
	const list = Array.isArray(data?.errors) ? data.errors : Array.isArray(data?.details) ? data.details : null;
	const errors: PricePlanValidationError[] = list
		? list.map((x: any) => ({
				partnerId: x?.partnerId ?? null,
				error: String(x?.error ?? (x?.field ? `${x.field}: ${x?.message ?? ""}` : x?.message) ?? x ?? ""),
		  }))
		: [];
	const message = String(data?.message || data?.code || (e as Error)?.message || "Request failed");
	return new PricePlanApiError(message, resp?.status ?? null, errors);
};

const call = async <T>(fn: () => Promise<{ data: any }>): Promise<T> => {
	try {
		const { data } = await fn();
		return data as T;
	} catch (e) {
		throw toPricePlanError(e);
	}
};

export const uploadPricePlan = async (plan: unknown): Promise<PricePlan> => {
	const data = unwrap(await call<any>(() => http.post(`/admin/price-plans`, { plan })));
	return mapPlan(data?.plan ?? data) as PricePlan;
};

export const getCurrentPricePlan = async (): Promise<PricePlan | null> => {
	const data = unwrap(await call<any>(() => http.get(`/admin/price-plans/current`)));
	return mapPlan(data && "plan" in (data as any) ? (data as any).plan : data);
};

export const applyPricePlan = async (
	id: PricePlan["id"],
	body: { hash: string; confirmBigChanges: boolean }
): Promise<PricePlanApplyResult> => {
	const d = unwrap(await call<any>(() => http.post(`/admin/price-plans/${encodeURIComponent(String(id))}/apply`, body)));
	return { ok: d?.verify ? !!d.verify.ok : true, applied: Number(d?.applied) || 0, durationMs: Number(d?.durationMs) || 0, snapshotId: d?.snapshotId };
};

export const getPricePlanHistory = async (): Promise<PricePlanHistoryItem[]> => {
	const d = unwrap(await call<any>(() => http.get(`/admin/price-plans/history`)));
	const items = Array.isArray(d) ? d : Array.isArray(d?.items) ? d.items : [];
	return items.map((h: any) => ({
		snapshotId: h.snapshotId,
		planId: h.planId ?? h.rollbackOf ?? null,
		appliedAt: h.appliedAt,
		appliedBy: who(h.appliedBy),
		count: Number(h.count) || 0,
		kind: (h.kind || h.type) === "rollback" ? "rollback" : "apply",
		status: h.status ?? null,
	}));
};

export const rollbackPricePlan = async (
	snapshotId: PricePlanHistoryItem["snapshotId"]
): Promise<PricePlanRollbackResult> => {
	const d = unwrap(
		await call<any>(() => http.post(`/admin/price-plans/history/${encodeURIComponent(String(snapshotId))}/rollback`, { confirm: true }))
	);
	return { ok: d?.verify ? !!d.verify.ok : true, applied: Number(d?.restored ?? d?.applied) || 0, snapshotId: d?.snapshotId };
};
